import React, { useState } from 'react';
import { BudgetProposalWithResourcesT } from 'routes/BudgetModule/config/types';
import { AutocompletAble } from 'ui/DropdownItems/types';
import FirstStep from './steps/FirstStep';
import SecondStep from './steps/SecondStep';
import { useDuplicateSettings } from './hooks';
import { useDispatch } from 'store/hooks';
import ConfirmationStep from '../../shared/ConfirmationStep';
import { useTranslation } from 'react-i18next';
import { trackEventDisplaySettingsDuplicationConfirm } from '../trackersHelpers';
import { StyMultiStepModal } from '../../shared/StyMultiStepModal';

type Props = {
    isModalShown: boolean;
    setShowModal: DispatchState<boolean>;
    budgetProposals: BudgetProposalWithResourcesT[];
};

export default function DuplicateSettingsModal({
    isModalShown,
    setShowModal,
    budgetProposals,
}: Props): React.ReactElement {
    const dispatch = useDispatch();
    const { t } = useTranslation();

    const [selectedSourceBudgetProposal, setSelectedSourceBudgetProposal] =
        useState<AutocompletAble<BudgetProposalWithResourcesT> | null>(null);
    const [
        selectedSourceResourceSoftwareId,
        setSelectedSourceResourceSoftwareId,
    ] = useState<AutocompletAble<string> | null>(null);
    const [selectedTargetBudgetProposals, setSelectedTargetBudgetProposals] =
        useState<AutocompletAble<BudgetProposalWithResourcesT>[] | null>(null);
    const [
        selectedTargetResourceSoftwareIds,
        setSelectedTargetResourceSoftwareIds,
    ] = useState<AutocompletAble<string>[] | null>(null);

    const { mutate: duplicateSettings } = useDuplicateSettings(dispatch);

    const onConfirmAction = () => {
        if (
            selectedSourceBudgetProposal == null ||
            selectedSourceResourceSoftwareId == null ||
            selectedTargetBudgetProposals == null ||
            selectedTargetResourceSoftwareIds == null
        )
            return;
        duplicateSettings({
            sourceBudgetProposalId: selectedSourceBudgetProposal.value.id,
            sourceResourceSoftwareId: selectedSourceResourceSoftwareId.value,
            targetBudgetProposalIds: selectedTargetBudgetProposals.map(
                (item) => item.value.id,
            ),
            targetResourceSoftwareIds: selectedTargetResourceSoftwareIds.map(
                (item) => item.value,
            ),
        });
        trackEventDisplaySettingsDuplicationConfirm(
            selectedSourceBudgetProposal.value.id,
            selectedSourceResourceSoftwareId.label,
            selectedTargetBudgetProposals.map((bp) => bp.value.id),
            selectedTargetResourceSoftwareIds.map((r) => r.value),
        );
    };

    const steps = [
        {
            confirmAction: {
                enabled:
                    selectedSourceBudgetProposal !== null &&
                    selectedSourceResourceSoftwareId !== null,
                label: 'common.next',
            },
            stepBackAction: {
                onClick: () => {
                    setShowModal(false);
                },
                enabled: true,
            },
            disableClose: true,
            node: (
                <FirstStep
                    selectedSourceBudgetProposal={selectedSourceBudgetProposal}
                    setSelectedSourceBudgetProposal={
                        setSelectedSourceBudgetProposal
                    }
                    selectedSourceResourceSoftwareId={
                        selectedSourceResourceSoftwareId
                    }
                    setSelectedSourceResourceSoftwareId={
                        setSelectedSourceResourceSoftwareId
                    }
                    budgetProposals={budgetProposals}
                />
            ),
        },
        {
            confirmAction: {
                enabled:
                    selectedTargetBudgetProposals !== null &&
                    selectedTargetResourceSoftwareIds !== null,
                label: 'common.next',
            },
            stepBackAction: {
                enabled: true,
            },
            disableClose: true,
            node: (
                <SecondStep
                    selectedTargetBudgetProposals={
                        selectedTargetBudgetProposals
                    }
                    setSelectedTargetBudgetProposals={
                        setSelectedTargetBudgetProposals
                    }
                    selectedTargetResourceSoftwareIds={
                        selectedTargetResourceSoftwareIds
                    }
                    setSelectedTargetResourceSoftwareIds={
                        setSelectedTargetResourceSoftwareIds
                    }
                    budgetProposals={budgetProposals}
                />
            ),
        },
        {
            confirmAction: {
                enabled: true,
                onClick: onConfirmAction,
                label: 'common.confirm',
            },
            stepBackAction: {
                enabled: true,
            },
            disableClose: true,
            node: (
                <ConfirmationStep
                    title={t('adminBudgetSpace.duplicateSettings.title')}
                    text={t('adminBudgetSpace.duplicateSettings.textConfirm')}
                />
            ),
        },
    ];
    return (
        <StyMultiStepModal
            isOpen={isModalShown}
            onClose={() => {
                setShowModal(false);
            }}
            maxWidth="md"
            steps={steps}
            showCloseIcon={true}
        />
    );
}
